import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as echarts from 'echarts';
import { format, parseISO } from 'date-fns';
import { InstalledCapacityResponse, DataType } from '../../../services/powerService';
import { useTheme } from '../../../theme/ThemeContext';

interface InstalledCapacityChartProps {
  capacityData: InstalledCapacityResponse;
  dataType: DataType;
}

// Define data categories and their display names
const dataCategories = {
  renewable: {
    title: 'Renewable Energy',
    sources: [
      { key: 'solar', label: 'Solar', color: '#FFCD29' },
      { key: 'wind_onshore', label: 'Wind Onshore', color: '#73C6B6' },
      { key: 'wind_offshore', label: 'Wind Offshore', color: '#16A085' },
      { key: 'biomass', label: 'Biomass', color: '#A3CB38' },
      { key: 'geothermal', label: 'Geothermal', color: '#E67E22' },
      { key: 'hydro_run_of_river', label: 'Hydro Run of River', color: '#3498DB' },
      { key: 'hydro_water_reservoir', label: 'Hydro Reservoir', color: '#2980B9' },
      { key: 'marine', label: 'Marine', color: '#21618C' },
      { key: 'other_renewables', label: 'Other Renewables', color: '#D4AC0D' },
    ],
  },
  conventional: {
    title: 'Conventional Energy',
    sources: [
      { key: 'nuclear', label: 'Nuclear', color: '#9B59B6' },
      { key: 'fossil_brown_coal', label: 'Brown Coal', color: '#8B5A2B' },
      { key: 'fossil_hard_coal', label: 'Hard Coal', color: '#555555' },
      { key: 'fossil_gas', label: 'Gas', color: '#E74C3C' },
      { key: 'fossil_oil', label: 'Oil', color: '#17202A' },
      { key: 'fossil_coal_derived_gas', label: 'Coal-derived Gas', color: '#7B7D7D' },
      { key: 'fossil_oil_shale', label: 'Oil Shale', color: '#2C3E50' },
      { key: 'fossil_peat', label: 'Peat', color: '#784212' },
      { key: 'other_non_renewable', label: 'Other Non-Renewable', color: '#566573' },
      { key: 'waste', label: 'Waste', color: '#5D6D7E' },
    ],
  },
  storage: {
    title: 'Storage',
    sources: [
      { key: 'hydro_pumped_storage', label: 'Pumped Hydro Storage', color: '#76D7C4' },
      { key: 'battery_storage_power', label: 'Battery Storage (Power)', color: '#1ABC9C' },
      { key: 'battery_storage_capacity', label: 'Battery Storage (Capacity)', color: '#45B39D' },
    ],
  },
};

const InstalledCapacityChart: React.FC<InstalledCapacityChartProps> = ({
  capacityData,
  dataType,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState<'renewable' | 'conventional' | 'all'>('all');

  // Define updateChart with useCallback to avoid dependency cycle
  const updateChart = useCallback(() => {
    if (!chartInstance.current || !capacityData || capacityData.data.length === 0) return;

    // Get text color based on theme
    const textColor = isDark ? '#e0e0e0' : '#333';
    const backgroundColor = isDark ? '#1F2937' : '#FFFFFF';

    // Transform data for chart
    const timestamps = capacityData.data.map((item) => {
      // Format the timestamp based on granularity
      const date = parseISO(item.timestamp);
      return capacityData.granularity === 'yearly'
        ? format(date, 'yyyy')
        : format(date, 'MMM yyyy');
    });

    // Prepare series data based on active tab
    let seriesData: echarts.SeriesOption[] = [];
    const allSources = [
      ...dataCategories.renewable.sources,
      ...dataCategories.conventional.sources,
      ...dataCategories.storage.sources,
    ];

    const visibleSources =
      activeTab === 'all'
        ? allSources
        : activeTab === 'renewable'
          ? dataCategories.renewable.sources
          : dataCategories.conventional.sources;

    // Chart type based on data type
    const chartType = dataType === 'total' ? 'bar' : 'line';
    const stack = dataType === 'total' ? 'total' : undefined;

    // Add generation sources
    seriesData = visibleSources.map((source) => {
      const series: any = {
        name: source.label,
        type: chartType,
        stack,
        emphasis: {
          focus: 'series',
        },
        itemStyle: {
          color: source.color,
        },
        data: capacityData.data.map((item) => {
          const value = item[source.key as keyof typeof item];
          return value === null || value === undefined ? '-' : value;
        }),
      };

      // Add bar width for total capacity to create stacked bar chart
      if (dataType === 'total') {
        series.barMaxWidth = 50;
      }

      return series;
    });

    // Chart title
    const title = `Installed Capacity - ${capacityData.country_name} (${capacityData.country_code})`;
    const subtitle = dataType === 'total' ? 'Total Capacity' : 'Capacity Changes';

    // Chart options
    const option: echarts.EChartsOption = {
      backgroundColor: backgroundColor,
      title: {
        text: title,
        subtext: subtitle,
        left: 'center',
        textStyle: {
          color: textColor,
        },
        subtextStyle: {
          color: isDark ? '#9CA3AF' : '#6B7280',
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: function (params: any) {
          if (!params || params.length === 0) return '';

          let tooltip = `<div style="margin: 0 0 10px 0;"><strong>${params[0].name}</strong></div>`;

          // For total capacity, calculate the total
          if (dataType === 'total') {
            const total = params.reduce((sum: number, item: any) => {
              const value = item.value === '-' ? 0 : Number(item.value);
              return sum + value;
            }, 0);

            tooltip += `<div style="margin-bottom: 5px;">
              <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#E34290;margin-right:5px;"></span>
              <span style="font-weight:bold;">Total: ${total.toFixed(2)} GW</span>
            </div>`;
          }

          // Add each item
          params.forEach((item: any) => {
            if (item.value !== '-') {
              tooltip += `<div style="display:flex;justify-content:space-between;margin: 3px 0;">
                <span style="display:inline-block;margin-right:5px;">
                  <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${item.color};margin-right:5px;"></span>
                  ${item.seriesName}:
                </span>
                <span style="float:right;font-weight:bold;">${Number(item.value).toFixed(2)} GW</span>
              </div>`;
            }
          });

          return tooltip;
        },
      },
      legend: {
        data: visibleSources.map((s) => s.label),
        bottom: 35,
        type: 'scroll',
        orient: 'horizontal',
        textStyle: {
          color: textColor,
        },
        pageButtonPosition: 'end',
        pageButtonGap: 10,
        tooltip: {
          show: true,
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '22%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: timestamps,
        axisLabel: {
          color: textColor,
        },
      },
      yAxis: {
        type: 'value',
        name: 'Capacity (GW)',
        nameTextStyle: {
          color: textColor,
        },
        axisLabel: {
          color: textColor,
          formatter: (value: any) => {
            return value.toFixed(1);
          },
        },
      },
      series: seriesData,
    };

    // Force a complete redraw of the chart with notMerge: true
    chartInstance.current.setOption(option, true);
  }, [capacityData, activeTab, isDark, dataType]);

  // Initialize or re-initialize chart when theme changes
  useEffect(() => {
    if (chartRef.current) {
      // Clean up previous instance if it exists
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }

      // Create new chart instance with correct theme
      chartInstance.current = echarts.init(chartRef.current, isDark ? 'dark' : undefined);
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
    };
  }, [isDark]);

  // Setup window resize handler
  useEffect(() => {
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Update chart whenever relevant props or state change
  useEffect(() => {
    if (chartInstance.current && capacityData && capacityData.data.length > 0) {
      updateChart();
    }
  }, [capacityData, activeTab, isDark, dataType, updateChart]);

  // Calculate the percentage of renewable capacity
  const getRenewablePercentage = (): number => {
    if (!capacityData || capacityData.data.length === 0) return 0;

    // Use the latest data point for the calculation
    const latestData = capacityData.data[capacityData.data.length - 1];

    // Calculate total renewable capacity
    const renewableCapacity = dataCategories.renewable.sources.reduce((sum, source) => {
      const value = latestData[source.key as keyof typeof latestData];
      return sum + (typeof value === 'number' ? value : 0);
    }, 0);

    // Calculate total conventional capacity
    const conventionalCapacity = dataCategories.conventional.sources.reduce((sum, source) => {
      const value = latestData[source.key as keyof typeof latestData];
      return sum + (typeof value === 'number' ? value : 0);
    }, 0);

    // Calculate percentage
    const totalCapacity = renewableCapacity + conventionalCapacity;
    return totalCapacity > 0 ? (renewableCapacity / totalCapacity) * 100 : 0;
  };

  // Handle tab changes
  const handleTabChange = (tab: 'all' | 'renewable' | 'conventional') => {
    setActiveTab(tab);
  };

  return (
    <div className="space-y-4">
      {dataType === 'total' && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            Renewable Energy Share
          </h3>
          <div className="flex items-center">
            <div className="relative w-full h-4 bg-gray-200 dark:bg-gray-600 rounded-full">
              <div
                className="absolute top-0 left-0 h-4 bg-gradient-to-r from-green-400 to-green-600 rounded-full"
                style={{ width: `${Math.min(getRenewablePercentage(), 100)}%` }}
              ></div>
            </div>
            <span className="ml-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {Math.round(getRenewablePercentage())}%
            </span>
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {Math.round(getRenewablePercentage())}% of the installed capacity in{' '}
            {capacityData.country_name} is from renewable sources.
          </p>
        </div>
      )}

      <div className="flex justify-center space-x-4 mb-4">
        <button
          onClick={() => handleTabChange('all')}
          className={`px-4 py-2 rounded-md ${
            activeTab === 'all'
              ? 'bg-[#E34290] text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}
        >
          All Sources
        </button>
        <button
          onClick={() => handleTabChange('renewable')}
          className={`px-4 py-2 rounded-md ${
            activeTab === 'renewable'
              ? 'bg-[#16A085] text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}
        >
          Renewables
        </button>
        <button
          onClick={() => handleTabChange('conventional')}
          className={`px-4 py-2 rounded-md ${
            activeTab === 'conventional'
              ? 'bg-[#E74C3C] text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}
        >
          Conventional
        </button>
      </div>
      <div ref={chartRef} style={{ height: '500px', width: '100%' }}></div>
    </div>
  );
};

export default InstalledCapacityChart;
